const Yup = require('yup');
const { isValidUSPhone } = require('./phone');

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const NOT_ONLY_WHITESPACE_REGEX = /^(?!\s*$).+$/;

const nameField = () =>
    Yup.string().required('Required').matches(NOT_ONLY_WHITESPACE_REGEX, 'Only spaces are not allowed');
const emailField = () => Yup.string().email('Invalid email').matches(EMAIL_REGEX, 'Invalid email').required('Required');
// A length check alone let "+1 126-261-3020" through: a leading 1 typed as the area code (+11 leads).
const phoneField = () =>
    Yup.string()
        .required('Required')
        .test({ name: 'us-phone', message: 'Invalid phone number', test: (value) => !value || isValidUSPhone(value) });
const locationField = () => Yup.string().required('Required');

const leadFields = () => ({
    first_name: nameField(),
    last_name: nameField(),
    email: emailField(),
    phone: phoneField(),
    location: locationField(),
});

const contactSchema = Yup.object().shape({
    ...leadFields(),
    description: Yup.string(),
});

const faqsSchema = Yup.object().shape({
    ...leadFields(),
    description: Yup.string().required('Required'),
});

const signUpSchema = Yup.object().shape(leadFields());

const couponSchema = Yup.object().shape(leadFields());

const specialsSchema = Yup.object().shape(leadFields());

const consultSchema = Yup.object().shape({
    ...leadFields(),
    description: Yup.string(),
    consultType: Yup.string(),
});

const QUOTE_DESCRIPTION_PREFIX = 'Instant Estimate: ';
const QUOTE_DESCRIPTION_SEPARATOR = ' | ';

const buildQuoteDescription = (bodyAreas, otherAreas) =>
    `${QUOTE_DESCRIPTION_PREFIX}${bodyAreas.join(QUOTE_DESCRIPTION_SEPARATOR)}${QUOTE_DESCRIPTION_SEPARATOR}${otherAreas}`;

const getQuoteOtherAreas = (bodyAreas, description) => {
    if (typeof description !== 'string') return description;
    const prefix = buildQuoteDescription(Array.isArray(bodyAreas) ? bodyAreas : [], '');
    return description.startsWith(prefix) ? description.slice(prefix.length) : description;
};

const quoteSchema = Yup.object().shape(
    {
        ...leadFields(),
        bodyAreas: Yup.array().when('description', ([description], schema) => {
            const limited = schema.max(3, 'No more than 3 body areas to be selected');
            return description
                ? limited
                : limited.min(1, 'At least one body area must be selected if the "Other Areas" field below is empty');
        }),
        description: Yup.string()
            .trim()
            .when('bodyAreas', ([bodyAreas], schema) =>
                bodyAreas && bodyAreas.length
                    ? schema
                    : schema.required('This field is required if none of the body areas above is selected')
            ),
    },
    [['bodyAreas', 'description']]
);

// Mirrors the Zod rules that validate the Brand Partner form in the browser:
// @milanlaser/mlcomponents 1.70.1, src/components/Sections/Forms/ConsultFormV2/ConsultFormV2.schemas.ts
// (its phone rule lives in src/utils/phoneFormatting.ts and matches isValidUSPhone in ./phone).
// The package does not export them, so re-check this block whenever that dependency is bumped.
const ZOD_EMAIL_REGEX = /^(?!\.)(?!.*\.\.)([A-Z0-9_'+\-\.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9\-]*\.)+[A-Z]{2,}$/i;

const brandPartnerEmailField = () =>
    Yup.string().required('Please enter your email').matches(ZOD_EMAIL_REGEX, 'Please enter a valid email');

const brandPartnerPhoneField = () =>
    Yup.string().required('Please enter your phone number').test({
        name: 'us-phone',
        message: 'Please enter a valid phone number',
        skipAbsent: true,
        test: isValidUSPhone,
    });

const brandPartnerFullSchema = Yup.object().shape({
    first_name: Yup.string().required('Please enter your first name'),
    last_name: Yup.string().required('Please enter your last name'),
    email: brandPartnerEmailField(),
    phone: brandPartnerPhoneField(),
    location: Yup.string().required('Please enter your location'),
});

const brandPartnerPartialSchema = Yup.object().shape({
    first_name: Yup.string(),
    last_name: Yup.string(),
    email: brandPartnerEmailField(),
    phone: brandPartnerPhoneField(),
    location: Yup.string(),
});

module.exports = {
    nameField,
    emailField,
    phoneField,
    contactSchema,
    quoteSchema,
    faqsSchema,
    signUpSchema,
    couponSchema,
    consultSchema,
    specialsSchema,
    brandPartnerFullSchema,
    brandPartnerPartialSchema,
    buildQuoteDescription,
    getQuoteOtherAreas,
};
